/**
 *  人员管理模块服务端、客户端数据转换
 */

import i18n from "@/i18n";
import { recursionTrans } from "../../common/util.js";
import { businessLangs } from "@/components/langSelect/languages.js";
import { highlightTitle } from "@/views/zhiji/js/common/constants"

function getStatusLabel(status) {
    const statusObj = { 0: i18n.t("zhiji.personnel.disablingLabel"), 1: i18n.t("zhiji.common.normal") };
    return statusObj[status] || "";
}

// 错误信息转换
function cvtRequestErrorData(error) {
    return {
        errCode: error.err_code,
        errMessage: error.err_msg,
        result: error.result
    };
}

/**********
 *  账号管理
 ***********/

// 账号管理列表请求参数
function cvtAccountParam(reqData) {
    return {
        member_id: reqData.accountId || "",
        name: reqData.accountName || "",
        email: reqData.email || "",
        page: reqData.page,
        size: reqData.size,
        group_id: reqData.group === "all" || reqData.group === undefined ? 0 : reqData.group
    };
}

// 账号新增保存请求参数
function cvtAccountSaveParam(reqData) {
    return {
        name: reqData.accountName,
        email: reqData.useEmail,
        role_id: reqData.roleId,
        groups: reqData.groups || 0,
        online_max_num: reqData.onlineMax,
        daily_issue_max_num: reqData.dayMax,
        member_id: reqData.memberId
    };
}

// 账号更新请求参数
function cvtAccountUpdateParam(reqData) {
    return {
        member_id: reqData.accountId,
        name: reqData.accountName,
        email: reqData.useEmail,
        role_id: reqData.roleId,
        groups: reqData.groups || 0,
        online_max_num: reqData.onlineMax,
        daily_issue_max_num: reqData.dayMax
    };
}

// 账号管理列表客户端数据格式
function accountData2Client(result) {
    let ret = {
        accountTotal: result.count,
        accountData: []
    };
    ret.accountData = result.list.map(item => {
        return {
            id: item.mid,
            userName: item.name,
            account: item.email,
            rowId: item.mid,
            userRole: item.role_name,
            userGroup: item.groups ? item.group_names : i18n.t("zhiji.personnel.defaultLable"),
            chatNum: item.online_max_num ? item.online_max_num : i18n.t("zhiji.personnel.defaultLable"),
            maxWork: item.daily_issue_max_num ? item.daily_issue_max_num : i18n.t("zhiji.personnel.defaultLable"),
            status: getStatusLabel(item.status),
            operate: getOperateArr(item),
            addData: {
                userName: item.name,
                account: item.email,
                userRole: item.role_id,
                userGroup: item.groups == "0" ? "all" : item.groups,
                chatNum: item.online_max_num,
                maxWork: item.daily_issue_max_num
            }
        };
    });
    return ret;
}

function getOperateArr(data) {
    if (data.status == 0) {
        return ["edit", "enable", "delete"];
    } else if (data.status == 1) {
        return ["edit", "disable", "delete"];
    }
    return [];
}

/**********
 *  分组管理
 ***********/

// 所有分组信息客户端数据格式
function allGroupOption2Client(result) {
    let ret = [
        {
            label: i18n.t("zhiji.personnel.defaultNotGroup"),
            value: "all"
        }
    ];

    ret.push(
        ...result.list.map(function(group) {
            return {
                label: group.group_name,
                value: group.group_id
            };
        })
    );

    return ret;
}

// 分组管理列表请求参数
function cvtGroupParam(reqData) {
    return {
        name: reqData.groupName || "",
        page: reqData.page,
        size: reqData.size
    };
}

// 分组新增保存请求参数
function cvtGroupSaveParam(reqData) {
    return {
        tag_id: reqData.tagId,
        group_name: reqData.groupName,
        users: reqData.users || "",
        lang: reqData.language,
        group_type: reqData.groupType,
        group_online_max_num: reqData.onlineMax || "",
        group_daily_issue_max_num: reqData.dayMax || ""
    };
}

// 组别更新请求参数
function cvtGroupUpdateParam(reqData) {
    return {
        tag_id: reqData.tagId,
        group_id: reqData.groupId,
        group_name: reqData.groupName,
        users: reqData.users || "",
        lang: reqData.language,
        group_type: reqData.groupType,
        group_online_max_num: reqData.onlineMax || "",
        group_daily_issue_max_num: reqData.dayMax || ""
    };
}

// 分组管理列表客户端数据格式
function groupData2Client(result) {
    let ret = {
        groupListTotal: result.count,
        groupData: []
    };
    let groupTypeObj = {
        0: i18n.t("zhiji.personnel.defaultGroup"),
        1: i18n.t("zhiji.personnel.groupManage.groupEditModal.workGroup"),
        2: i18n.t("zhiji.personnel.groupManage.groupEditModal.chatGroup"),
        3: i18n.t("zhiji.personnel.groupManage.groupEditModal.workGroup") + "/" + i18n.t("zhiji.personnel.groupManage.groupEditModal.chatGroup")
    };

    ret.groupData = result.list.map(item => {
        return {
            id: item.group_id,
            groupName: item.group_name,
            member: item.users_name,
            language: getCurLang(item.lang),
            rowId: item.group_id,
            classify: groupTypeObj[item.group_type],
            doMax: item.group_daily_issue_max_num,
            status: getStatusLabel(item.status),
            operate: getOperateArr(item),
            tagName: `${item.parent_tag_name} > ${item.tag_name}`,
            addData: {
                tagId: item.tag_id,
                groupName: item.group_name,
                member: item.users,
                language: item.lang,
                rowId: item.group_id,
                classify: item.group_type,
                doMax: item.group_daily_issue_max_num,
                chatMax: item.group_online_max_num
            }
        };
    });
    return ret;
}

function getCurLang(lang) {
    let curLang = businessLangs().filter(item => {
        return item.lang === lang;
    });

    return curLang.length > 0 ? i18n.t(curLang[0].i18nKey) : "";
}

/**********
 *  角色管理
 ***********/

// 所有角色信息客户端数据格式
function allRoleOption2Client(result) {
    let ret = [
        {
            label: i18n.t("zhiji.personnel.roleManage.roleEditModal.selectRoleTip"),
            value: ""
        }
    ];

    ret.push(
        ...result.list.map(function(group) {
            return {
                label: group.role_name,
                value: group.role_id
            };
        })
    );

    return ret;
}

// 角色管理列表请求参数
function cvtRoleListParam(reqData) {
    return {
        page: reqData.page,
        size: reqData.size
    };
}

// 角色管理列表客户端数据格式
function roleListData2Client(result) {
    let ret = {
        roleListTotal: result.count,
        roleData: []
    };
    ret.roleData = result.list.map(item => {
        return {
            id: item.role_id,
            roleName: item.role_name,
            perModule: item.permit_names,
            remarks: item.remark,
            rowId: item.role_id,
            status: getStatusLabel(item.status),
            operate: getOperateArr(item),
            addData: {
                roleName: item.role_name,
                premit: item.permit,
                remarks: item.remark,
                rowId: item.role_id,
            }
        };
    });
    return ret;
}

// 角色新增保存请求参数
function cvtRoleSaveParam(reqData) {
    return {
        role_name: reqData.roleName,
        remark: reqData.remarkData,
        permit: reqData.permit
    };
}

// 角色更新请求参数
function cvtRoleUpdateParam(reqData) {
    return {
        openid: reqData.openid || "",
        role_id: reqData.roleId,
        role_name: reqData.roleName,
        remark: reqData.remarkData,
        permit: reqData.permit
    };
}

// 权限列表客户端数据格式
function permitList2Client(result) {
    return recursionTrans(
        result,
        function(permitData, parent) {
            return {
                id: "" + permitData.id,
                label: permitData.name,
                level: permitData.hierarchy,
                text: permitData.text,
                parentId: permitData.pid,
                path: parent ? `${parent.path}/${permitData.id}` : permitData.id,
            };
        },
        "nodes",
        "children"
    );
}

// 员工操作日志Ulog

function UlogSearchTypeCvt(result) {
    const searchTypeObj = {
        '新增人员管理': i18n.t("zhiji.personnel.ulogManage.typeList.newPerson"),
        '新增标准问法': i18n.t("zhiji.personnel.ulogManage.typeList.newStandardQuestions"),
        '新增答案': i18n.t("zhiji.personnel.ulogManage.typeList.newAnswer"),
        '删除标准问法': i18n.t("zhiji.personnel.ulogManage.typeList.deleteQuestion"),
        '批量新增标问问法': i18n.t("zhiji.personnel.ulogManage.typeList.addQuesInBatch"),
        '新增相似问法': i18n.t("zhiji.personnel.ulogManage.typeList.addSimilarQues"),
        '批量新增知识库': i18n.t("zhiji.personnel.ulogManage.typeList.batchImport"),
        '新增分类': i18n.t("zhiji.personnel.ulogManage.typeList.addcategories"),
        '删除分类': i18n.t("zhiji.personnel.ulogManage.typeList.deleteCategories"),
        '新增表单': i18n.t("zhiji.personnel.ulogManage.typeList.addForm"),
        '删除表单': i18n.t("zhiji.personnel.ulogManage.typeList.deleteForm"),
        '导出问答日志': i18n.t("zhiji.personnel.ulogManage.typeList.exportlog"),
        '导出场景满意度': i18n.t("zhiji.personnel.ulogManage.typeList.exportSceneSatisfaction"),
        '导出问题满意度': i18n.t("zhiji.personnel.ulogManage.typeList.exportProblemSatisfaction"),
        '导出机器人数据': i18n.t("zhiji.personnel.ulogManage.typeList.exportRobotDataDetails"),
        '导出场景热点': i18n.t("zhiji.personnel.ulogManage.typeList.exportHotTopicCategory"),
        '导出问题热点': i18n.t("zhiji.personnel.ulogManage.typeList.exportTotTopicQueriescial"),
        '删除人员管理': i18n.t("zhiji.personnel.ulogManage.typeList.deleteAccount"),
        '删除聚类': i18n.t("zhiji.personnel.ulogManage.typeList.DeleteSetQuestion"),
        '删除闭环': i18n.t("zhiji.personnel.ulogManage.typeList.deleteSetQuestion"),
        '聚类闭环：关联问法': i18n.t("zhiji.personnel.ulogManage.typeList.relateSimilarQuestion"),
        '聚类闭环：删除问法': i18n.t("zhiji.personnel.ulogManage.typeList.deleteRealteQuestion"),
        '修改标准问法': i18n.t("zhiji.personnel.ulogManage.typeList.updatedStandardQuestions"),
        '删除相似问法': i18n.t("zhiji.personnel.ulogManage.typeList.deleteSimilarQuestions"),
        '导出FAQ': i18n.t("zhiji.personnel.ulogManage.typeList.exportFAQ"),
        '问题闭环：关联问法': i18n.t("zhiji.personnel.ulogManage.typeList.relateQuestion"),
        '启用表单': i18n.t("zhiji.personnel.ulogManage.typeList.enableForm"),
        '停用表单': i18n.t("zhiji.personnel.ulogManage.typeList.disableForm"),
        '新增一级分类': i18n.t("zhiji.personnel.ulogManage.typeList.addFirstLevel"),
        '新增三级分类': i18n.t("zhiji.personnel.ulogManage.typeList.addthirdLevel"),
        '上线标准问法': i18n.t("zhiji.personnel.ulogManage.typeList.onlineQs"),
        '下线标准问法': i18n.t("zhiji.personnel.ulogManage.typeList.offlineQs"),
        '新增二级分类': i18n.t("zhiji.personnel.ulogManage.typeList.addSecondLevel"),
        '修改人员管理': i18n.t("zhiji.personnel.ulogManage.typeList.modifyStuff"),
        '导出服务数据报表': i18n.t("zhiji.personnel.ulogManage.typeList.exportSeviceData"),
        '导出机器人监控数据趋势': i18n.t("zhiji.personnel.ulogManage.typeList.exportTrend"),
        '修改答案': i18n.t("zhiji.personnel.ulogManage.typeList.modifyAnswers"),
        '修改表单': i18n.t("zhiji.personnel.ulogManage.typeList.modifyForms"),
        '转移标准问法': i18n.t("zhiji.personnel.ulogManage.typeList.transfetStandardQs"),
        '修改分类': i18n.t("zhiji.personnel.ulogManage.typeList.transfetStandardQs"),
        '闭环新增标准问法': i18n.t("zhiji.personnel.ulogManage.typeList.closeAddQuestion"),
        '聚类新增标准问法': i18n.t("zhiji.personnel.ulogManage.typeList.AddQuestions"),
        '闭环关联标准问法': i18n.t("zhiji.personnel.ulogManage.typeList.relateSimilarQuestion"),
        '聚类关联标准问法': i18n.t("zhiji.personnel.ulogManage.typeList.relatesimilar"),
        '删除场景分类': i18n.t("zhiji.personnel.ulogManage.typeList.deleteScene"),
        '创建场景分类': i18n.t("zhiji.personnel.ulogManage.typeList.addScene"),
    }
    // 处理搜索类型select的国际化
    return result.map(item => {
        let typeList = {
            show:searchTypeObj[item.show],
            value:item.value
        }
        return  typeList
    })
}

function UlogDataTypeCvt(result, count) {
    const searchTypeObj = {
        '新增人员管理': i18n.t("zhiji.personnel.ulogManage.typeList.newPerson"),
        '新增标准问法': i18n.t("zhiji.personnel.ulogManage.typeList.newStandardQuestions"),
        '新增答案': i18n.t("zhiji.personnel.ulogManage.typeList.newAnswer"),
        '删除标准问法': i18n.t("zhiji.personnel.ulogManage.typeList.deleteQuestion"),
        '批量新增标问问法': i18n.t("zhiji.personnel.ulogManage.typeList.addQuesInBatch"),
        '新增相似问法': i18n.t("zhiji.personnel.ulogManage.typeList.addSimilarQues"),
        '批量新增知识库': i18n.t("zhiji.personnel.ulogManage.typeList.batchImport"),
        '新增分类': i18n.t("zhiji.personnel.ulogManage.typeList.addcategories"),
        '删除分类': i18n.t("zhiji.personnel.ulogManage.typeList.deleteCategories"),
        '新增表单': i18n.t("zhiji.personnel.ulogManage.typeList.addForm"),
        '删除表单': i18n.t("zhiji.personnel.ulogManage.typeList.deleteForm"),
        '导出机器人问答日志': i18n.t("zhiji.personnel.ulogManage.typeList.exportBotLog"),
        '导出场景满意度': i18n.t("zhiji.personnel.ulogManage.typeList.exportSceneSatisfaction"),
        '导出问题满意度': i18n.t("zhiji.personnel.ulogManage.typeList.exportProblemSatisfaction"),
        '导出机器人数据': i18n.t("zhiji.personnel.ulogManage.typeList.exportRobotDataDetails"),
        '导出场景热点': i18n.t("zhiji.personnel.ulogManage.typeList.exportHotTopicCategory"),
        '导出问题热点': i18n.t("zhiji.personnel.ulogManage.typeList.exportTotTopicQueriescial"),
        '删除人员管理': i18n.t("zhiji.personnel.ulogManage.typeList.deleteAccount"),
        '删除聚类': i18n.t("zhiji.personnel.ulogManage.typeList.DeleteSetQuestion"),
        '删除闭环': i18n.t("zhiji.personnel.ulogManage.typeList.deleteSetQuestion"),
        '聚类闭环：关联问法': i18n.t("zhiji.personnel.ulogManage.typeList.relateSimilarQuestion"),
        '聚类闭环：删除问法': i18n.t("zhiji.personnel.ulogManage.typeList.deleteRealteQuestion"),
        '删除相似问法': i18n.t("zhiji.personnel.ulogManage.typeList.deleteSimilarQuestions"),
        '修改标准问法': i18n.t("zhiji.personnel.ulogManage.typeList.updatedStandardQuestions"),
        '导出FAQ': i18n.t("zhiji.personnel.ulogManage.typeList.exportFAQ"),
        '问题闭环：关联问法': i18n.t("zhiji.personnel.ulogManage.typeList.relateQuestion"),
        '启用表单': i18n.t("zhiji.personnel.ulogManage.typeList.enableForm"),
        '停用表单': i18n.t("zhiji.personnel.ulogManage.typeList.disableForm"),
        '导出问答日志': i18n.t("zhiji.personnel.ulogManage.typeList.exportlog"),
        '新增一级场景分类': i18n.t("zhiji.personnel.ulogManage.typeList.addFirstLevel"),
        '新增三级场景分类': i18n.t("zhiji.personnel.ulogManage.typeList.addthirdLevel"),
        '上线标准问法': i18n.t("zhiji.personnel.ulogManage.typeList.onlineQs"),
        '下线标准问法': i18n.t("zhiji.personnel.ulogManage.typeList.offlineQs"),
        '新增二级场景分类': i18n.t("zhiji.personnel.ulogManage.typeList.addSecondLevel"),
        '修改人员管理': i18n.t("zhiji.personnel.ulogManage.typeList.modifyStuff"),
        '导出服务数据报表': i18n.t("zhiji.personnel.ulogManage.typeList.exportSeviceData"),
        '导出机器人监控数据趋势': i18n.t("zhiji.personnel.ulogManage.typeList.exportTrend"),
        '修改答案': i18n.t("zhiji.personnel.ulogManage.typeList.modifyAnswers"),
        '修改表单': i18n.t("zhiji.personnel.ulogManage.typeList.modifyForms"),
        '转移标准问法': i18n.t("zhiji.personnel.ulogManage.typeList.transfetStandardQs"),
        '修改分类': i18n.t("zhiji.personnel.ulogManage.typeList.transfetStandardQs"),
        '闭环新增标准问法': i18n.t("zhiji.personnel.ulogManage.typeList.closeAddQuestion"),
        '聚类新增标准问法': i18n.t("zhiji.personnel.ulogManage.typeList.AddQuestions"),
        '闭环关联标准问法': i18n.t("zhiji.personnel.ulogManage.typeList.relateSimilarQuestion"),
        '聚类关联标准问法': i18n.t("zhiji.personnel.ulogManage.typeList.relatesimilar"),
        '删除场景分类': i18n.t("zhiji.personnel.ulogManage.typeList.deleteScene"),
        '创建场景分类': i18n.t("zhiji.personnel.ulogManage.typeList.addScene"),
    }
    // 处理table里type数据列的国际化
    let tableData = result.map(item => {
        let typeList = {
            operation_type: searchTypeObj[item.operation_type],
            create_time: item.create_time,
            user: item.user,
            question: item.question,
            detail: item.detail,
            optContent: item.operate_content,
            id: item.id
        }
        return typeList
    })
    return { list: tableData, total: count}
}

export default {
    cvtRequestErrorData,
    cvtAccountParam,
    accountData2Client,
    cvtGroupParam,
    groupData2Client,
    cvtRoleListParam,
    roleListData2Client,
    allGroupOption2Client,
    allRoleOption2Client,
    cvtAccountSaveParam,
    cvtAccountUpdateParam,
    cvtGroupSaveParam,
    cvtGroupUpdateParam,
    cvtRoleSaveParam,
    cvtRoleUpdateParam,
    permitList2Client,
    UlogSearchTypeCvt,
    UlogDataTypeCvt
};
